import Pusher from 'pusher-js';
import React from 'react';

// Pusher konfigurációs objektum
interface PusherConfig {
  key: string;
  cluster: string;
  forceTLS: boolean;
}

// Admin események típusai
export interface AdminEvent {
  type: 'user_action' | 'game_event' | 'payment' | 'system' | 'security';
  data: any;
  timestamp: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  userId?: string;
  adminId?: string;
}

// Játék események típusai
export interface GameEvent {
  gameId: string;
  players: string[];
  action: 'started' | 'completed' | 'abandoned' | 'ball_pocketed' | 'turn_change';
  data: any;
  timestamp: number;
}

// Fizetési események típusai
export interface PaymentEvent {
  transactionId: string;
  userId: string;
  amount: number;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  paymentMethod: string;
  timestamp: number;
}

class PusherService {
  private pusher: Pusher | null = null;
  private channels: Map<string, any> = new Map();
  private isInitialized = false;
  private eventListeners: Map<string, Function[]> = new Map();
  private notificationPermission: NotificationPermission = 'default';
  private channelBindings: Set<string> = new Set();

  // Pusher inicializálása
  initialize() {
    if (this.isInitialized) return;

    const config: PusherConfig = {
      key: '08e5e271781df1fa6f1e',
      cluster: 'eu',
      forceTLS: true,
    };

    try {
      this.pusher = new Pusher(config.key, {
        cluster: config.cluster,
        forceTLS: config.forceTLS,
      });

      // Kapcsolat események
      this.pusher.connection.bind('connected', () => {
        console.log('✅ Pusher connected successfully');
        this.isInitialized = true;
        this.emit('pusher:connected', { timestamp: Date.now() });
      });

      this.pusher.connection.bind('disconnected', () => {
        console.log('❌ Pusher disconnected');
        this.emit('pusher:disconnected', { timestamp: Date.now() });
      });

      this.pusher.connection.bind('error', (error: any) => {
        console.error('🚨 Pusher connection error:', error);
        this.emit('pusher:error', { error, timestamp: Date.now() });
      });

      // Admin csatorna feliratkozás
      this.subscribeToAdminChannel();

      // Böngésző értesítések inicializálása
      this.initializeNotifications();
      
      console.log('🚀 Pusher service initialized');
    } catch (error) {
      console.error('Failed to initialize Pusher:', error);
    }
  }

  // Böngésző értesítések inicializálása
  private async initializeNotifications() {
    if (!('Notification' in window)) {
      console.warn('This browser does not support desktop notifications');
      return;
    }

    this.notificationPermission = Notification.permission;
    
    if (this.notificationPermission === 'default') {
      try {
        const permission = await Notification.requestPermission();
        this.notificationPermission = permission;
        console.log('🔔 Notification permission:', permission);
        this.emit('notification:permission', { permission, timestamp: Date.now() });
      } catch (error) {
        console.error('Error requesting notification permission:', error);
      }
    }
  }

  // Böngésző értesítés megjelenítése
  private showBrowserNotification(title: string, options: NotificationOptions = {}) {
    if (this.notificationPermission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    try {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        ...options
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      // Auto close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }

  // Admin csatorna feliratkozás
  private subscribeToAdminChannel() {
    const adminChannel = this.subscribe('admin-notifications');
    if (!adminChannel) return;

    // Rendszer események - csak ha még nincs feliratkozva
    const systemAlertKey = 'admin-notifications:system-alert';
    if (!this.channelBindings.has(systemAlertKey)) {
      adminChannel.bind('system-alert', (data: AdminEvent) => {
        this.emit('admin:system-alert', data);
        
        // Böngésző értesítés kritikus eseményeknél
        if (data.severity === 'critical' || data.severity === 'high') {
          this.showBrowserNotification(
            '🚨 Rendszer Riasztás',
            {
              body: data.data.message || 'Kritikus rendszer esemény történt',
              tag: 'system-alert',
              requireInteraction: data.severity === 'critical'
            }
          );
        }
      });
      this.channelBindings.add(systemAlertKey);
    }

    // Felhasználói műveletek
    const userActionKey = 'admin-notifications:user-action';
    if (!this.channelBindings.has(userActionKey)) {
      adminChannel.bind('user-action', (data: AdminEvent) => {
        this.emit('admin:user-action', data);
      });
      this.channelBindings.add(userActionKey);
    }

    // Biztonsági események
    const securityEventKey = 'admin-notifications:security-event';
    if (!this.channelBindings.has(securityEventKey)) {
      adminChannel.bind('security-event', (data: AdminEvent) => {
        this.emit('admin:security-event', data);
        
        // Biztonsági eseményeknél mindig böngésző értesítés
        this.showBrowserNotification(
          '🛡️ Biztonsági Esemény',
          {
            body: data.data.message || 'Biztonsági incidens észlelve',
            tag: 'security-event',
            requireInteraction: true
          }
        );
      });
      this.channelBindings.add(securityEventKey);
    }

    // Fizetési események
    const paymentEventKey = 'admin-notifications:payment-event';
    if (!this.channelBindings.has(paymentEventKey)) {
      adminChannel.bind('payment-event', (data: PaymentEvent) => {
        this.emit('admin:payment-event', data);
        
        // Nagy összegű vagy sikertelen fizetéseknél értesítés
        if (data.amount >= 50 || data.status === 'failed') {
          const isFailure = data.status === 'failed';
          this.showBrowserNotification(
            isFailure ? '❌ Sikertelen Fizetés' : '💰 Nagy Összegű Fizetés',
            {
              body: `${data.userId}: $${data.amount} (${data.status})`,
              tag: 'payment-event'
            }
          );
        }
      });
      this.channelBindings.add(paymentEventKey);
    }
  }

  // Játék események csatorna
  subscribeToGameEvents() {
    const gameChannel = this.subscribe('game-events');
    if (!gameChannel) return null;

    const gameStartedKey = 'game-events:game-started';
    if (!this.channelBindings.has(gameStartedKey)) {
      gameChannel.bind('game-started', (data: GameEvent) => {
        this.emit('game:started', data);
      });
      this.channelBindings.add(gameStartedKey);
    }

    const gameCompletedKey = 'game-events:game-completed';
    if (!this.channelBindings.has(gameCompletedKey)) {
      gameChannel.bind('game-completed', (data: GameEvent) => {
        this.emit('game:completed', data);
      });
      this.channelBindings.add(gameCompletedKey);
    }

    const gameAbandonedKey = 'game-events:game-abandoned';
    if (!this.channelBindings.has(gameAbandonedKey)) {
      gameChannel.bind('game-abandoned', (data: GameEvent) => {
        this.emit('game:abandoned', data);
      });
      this.channelBindings.add(gameAbandonedKey);
    }

    const ballPocketedKey = 'game-events:ball-pocketed';
    if (!this.channelBindings.has(ballPocketedKey)) {
      gameChannel.bind('ball-pocketed', (data: GameEvent) => {
        this.emit('game:ball-pocketed', data);
      });
      this.channelBindings.add(ballPocketedKey);
    }

    return gameChannel;
  }

  // Live statisztikák csatorna
  subscribeToLiveStats() {
    const statsChannel = this.subscribe('live-stats');
    if (!statsChannel) return null;

    const userCountKey = 'live-stats:user-count-update';
    if (!this.channelBindings.has(userCountKey)) {
      statsChannel.bind('user-count-update', (data: any) => {
        this.emit('stats:user-count', data);
      });
      this.channelBindings.add(userCountKey);
    }

    const activeGamesKey = 'live-stats:active-games-update';
    if (!this.channelBindings.has(activeGamesKey)) {
      statsChannel.bind('active-games-update', (data: any) => {
        this.emit('stats:active-games', data);
      });
      this.channelBindings.add(activeGamesKey);
    }

    const revenueKey = 'live-stats:revenue-update';
    if (!this.channelBindings.has(revenueKey)) {
      statsChannel.bind('revenue-update', (data: any) => {
        this.emit('stats:revenue', data);
      });
      this.channelBindings.add(revenueKey);
    }

    return statsChannel;
  }

  // Csatorna feliratkozás
  subscribe(channelName: string) {
    if (!this.pusher) {
      console.error('Pusher not initialized');
      return null;
    }

    if (!this.channels.has(channelName)) {
      const channel = this.pusher.subscribe(channelName);
      this.channels.set(channelName, channel);
      console.log(`📡 Subscribed to channel: ${channelName}`);
    }

    return this.channels.get(channelName);
  }

  // Csatorna leiratkozás
  unsubscribe(channelName: string) {
    if (this.channels.has(channelName)) {
      this.pusher?.unsubscribe(channelName);
      this.channels.delete(channelName);
      console.log(`📡 Unsubscribed from channel: ${channelName}`);
    }
  }

  // Esemény listener hozzáadása
  on(eventName: string, callback: Function) {
    if (!this.eventListeners.has(eventName)) {
      this.eventListeners.set(eventName, []);
    }
    this.eventListeners.get(eventName)?.push(callback);
  }

  // Esemény listener eltávolítása
  off(eventName: string, callback?: Function) {
    if (!callback) {
      this.eventListeners.delete(eventName);
    } else {
      const listeners = this.eventListeners.get(eventName);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    }
  }

  // Esemény kiváltása
  private emit(eventName: string, data: any) {
    const listeners = this.eventListeners.get(eventName);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${eventName}:`, error);
        }
      });
    }
  }

  // Mock események teszt célokra
  triggerTestEvents() {
    if (!this.isInitialized) {
      console.warn('Pusher not initialized, creating mock events');
    }

    // Mock admin események
    setTimeout(() => {
      this.emit('admin:user-action', {
        type: 'user_action',
        data: {
          action: 'login',
          userId: 'user123@example.com',
          timestamp: Date.now()
        },
        timestamp: Date.now(),
        severity: 'low'
      });
    }, 2000);

    setTimeout(() => {
      this.emit('admin:payment-event', {
        transactionId: 'tx_' + Date.now(),
        userId: 'user456@example.com',
        amount: 9.99,
        status: 'completed',
        paymentMethod: 'credit_card',
        timestamp: Date.now()
      });
    }, 4000);

    setTimeout(() => {
      this.emit('game:started', {
        gameId: 'game_' + Date.now(),
        players: ['player1@example.com', 'player2@example.com'],
        action: 'started',
        data: { level: 100, betAmount: 100 },
        timestamp: Date.now()
      });
    }, 6000);

    setTimeout(() => {
      this.emit('admin:system-alert', {
        type: 'system',
        data: {
          message: 'Server CPU usage above 80%',
          level: 'warning'
        },
        timestamp: Date.now(),
        severity: 'high'
      });
    }, 8000);
  }

  // Kapcsolat állapot lekérdezése
  getConnectionState() {
    return this.pusher?.connection.state || 'disconnected';
  }

  // Feliratkozott csatornák listája
  getSubscribedChannels() {
    return Array.from(this.channels.keys());
  }

  // Channel bindings száma
  getBindingsCount() {
    return this.channelBindings.size;
  }

  // Értesítési engedély állapota
  getNotificationPermission() {
    return this.notificationPermission;
  }

  // Értesítési engedély kérése
  async requestNotificationPermission() {
    if (!('Notification' in window)) {
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      this.notificationPermission = permission;
      this.emit('notification:permission', { permission, timestamp: Date.now() });
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  // Manuális böngésző értesítés
  showNotification(title: string, options: NotificationOptions = {}) {
    return this.showBrowserNotification(title, options);
  }

  // Pusher leállítása
  disconnect() {
    if (this.pusher) {
      this.channels.forEach((_, channelName) => {
        this.unsubscribe(channelName);
      });
      this.pusher.disconnect();
      this.isInitialized = false;
      this.channelBindings.clear(); // Bindings törlése
      console.log('🔌 Pusher disconnected');
    }
  }
}

// Singleton instance
export const pusherService = new PusherService();

// Admin események hook-ja
export const useAdminPusher = () => {
  const [isConnected, setIsConnected] = React.useState(false);
  const [events, setEvents] = React.useState<AdminEvent[]>([]);
  const [gameEvents, setGameEvents] = React.useState<GameEvent[]>([]);
  const [paymentEvents, setPaymentEvents] = React.useState<PaymentEvent[]>([]);
  const [notificationPermission, setNotificationPermission] = React.useState<NotificationPermission>('default');

  React.useEffect(() => {
    pusherService.initialize();

    // Kapcsolat események
    pusherService.on('pusher:connected', () => setIsConnected(true));
    pusherService.on('pusher:disconnected', () => setIsConnected(false));

    // Notification permission események
    pusherService.on('notification:permission', (data: any) => {
      setNotificationPermission(data.permission);
    });

    // Admin események
    pusherService.on('admin:system-alert', (event: AdminEvent) => {
      setEvents(prev => [event, ...prev.slice(0, 99)]); // Max 100 esemény
    });

    pusherService.on('admin:user-action', (event: AdminEvent) => {
      setEvents(prev => [event, ...prev.slice(0, 99)]);
    });

    pusherService.on('admin:security-event', (event: AdminEvent) => {
      setEvents(prev => [event, ...prev.slice(0, 99)]);
    });

    // Játék események
    pusherService.on('game:started', (event: GameEvent) => {
      setGameEvents(prev => [event, ...prev.slice(0, 49)]);
    });

    pusherService.on('game:completed', (event: GameEvent) => {
      setGameEvents(prev => [event, ...prev.slice(0, 49)]);
    });

    // Fizetési események
    pusherService.on('admin:payment-event', (event: PaymentEvent) => {
      setPaymentEvents(prev => [event, ...prev.slice(0, 49)]);
    });

    // Feliratkozás további csatornákra
    pusherService.subscribeToGameEvents();
    pusherService.subscribeToLiveStats();

    // Initial notification permission állapot
    setNotificationPermission(pusherService.getNotificationPermission());

    // Cleanup
    return () => {
      pusherService.disconnect();
    };
  }, []);

  return {
    isConnected,
    events,
    gameEvents,
    paymentEvents,
    notificationPermission,
    triggerTestEvents: pusherService.triggerTestEvents.bind(pusherService),
    getConnectionState: pusherService.getConnectionState.bind(pusherService),
    getSubscribedChannels: pusherService.getSubscribedChannels.bind(pusherService),
    getBindingsCount: pusherService.getBindingsCount.bind(pusherService),
    requestNotificationPermission: pusherService.requestNotificationPermission.bind(pusherService),
    showNotification: pusherService.showNotification.bind(pusherService)
  };
};

export default pusherService; 